import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";

export default function NewsDetail() {
  const { id } = useParams();
  const [art, setArt] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/news/${id}`);
        setArt(data);
        if (data.category_slug) {
          const r = await api.get(`/news?category=${data.category_slug}&limit=4`);
          setRelated(r.data.filter((x) => x.id !== id).slice(0, 3));
        }
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return (
    <div className="pt-32 text-center text-zinc-500 font-mono">Loading article...</div>
  );
  if (!art) return (
    <div className="pt-32 text-center text-zinc-500 font-mono">Article not found.</div>
  );

  return (
    <div className="pt-24 pb-20" data-testid="news-detail">
      {/* Hero Image */}
      {art.image_url && (
        <div className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
          <img src={art.image_url} alt={art.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        </div>
      )}

      {/* Article */}
      <article className={`max-w-3xl mx-auto px-6 ${art.image_url ? "-mt-40 relative z-10" : "mt-10"}`}>
        {art.category_slug && (
          <Link
            to={`/categories/${art.category_slug}`}
            className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-[0.2em] bg-yellow-400 text-black hover:bg-yellow-500 transition"
          >
            {art.category_slug.replace(/-/g, " ")}
          </Link>
        )}

        <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter mt-4 leading-[1.05]">
          {art.title}
        </h1>

        <div className="mt-5 flex items-center gap-3 text-sm font-mono text-zinc-400 flex-wrap">
          <span className="text-zinc-300">{art.author_name}</span>
          <span>•</span>
          <span>{new Date(art.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          <span>•</span>
          <span>{art.views || 0} views</span>
        </div>

        {art.excerpt && (
          <p className="mt-8 text-xl text-zinc-300 leading-relaxed font-medium border-l-4 border-yellow-400 pl-6 italic">
            {art.excerpt}
          </p>
        )}

        {/* Article Body */}
        <div className="mt-10 space-y-5">
          {art.content.split("\n\n").map((para, i) =>
            para.trim() ? (
              <p key={i} className="text-zinc-300 text-lg leading-8">
                {para.trim()}
              </p>
            ) : null
          )}
        </div>

        {/* Tags */}
        {art.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-2">
            {art.tags.map((t) => (
              <span key={t} className="px-3 py-1 bg-white/5 border border-white/10 text-xs font-mono uppercase text-zinc-400 hover:text-yellow-400 hover:border-yellow-400/30 transition cursor-pointer">
                #{t}
              </span>
            ))}
          </div>
        )}

        {/* Back link */}
        <div className="mt-10">
          <Link to="/news" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-yellow-400 transition">
            ← Back to News
          </Link>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
          <h3 className="font-display font-bold text-2xl text-white border-b border-white/10 pb-4 mb-8">
            <span className="text-yellow-400">/</span> Related Stories
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/news/${r.id}`}
                className="block bg-[#0a0a0b] border border-white/10 hover:border-yellow-400/40 transition p-5 group"
              >
                {r.image_url && (
                  <img src={r.image_url} alt={r.title} className="w-full h-32 object-cover mb-4" />
                )}
                <h4 className="font-display font-bold text-lg text-white group-hover:text-yellow-400 transition">{r.title}</h4>
                <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{r.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}